import React, { useState, useRef } from 'react';
import { Box, Typography, Paper, LinearProgress, IconButton, Alert, Card, CardMedia, CardContent } from '@mui/material';
import { CloudUpload as CloudUploadIcon, Delete as DeleteIcon, Image as ImageIcon } from '@mui/icons-material';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../firebase';

interface LogoUploadProps {
  currentLogoUrl?: string;
  onLogoChange: (logoUrl: string | null, storagePath?: string) => void;
  disabled?: boolean;
}

interface UploadState {
  file: File;
  progress: number;
  status: 'uploading' | 'completed' | 'error';
  downloadURL?: string;
  error?: string;
  storagePath?: string;
}

const LogoUpload: React.FC<LogoUploadProps> = ({ currentLogoUrl, onLogoChange, disabled = false }) => {
  const [upload, setUpload] = useState<UploadState | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isValidImageFile = (file: File): boolean => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const extension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    return ['.jpg', '.jpeg', '.png', '.webp'].includes(extension) && file.size <= maxSize;
  };

  const sanitizeStorageSegment = (name: string, fallback = 'logo') => {
    const leafName = name.split(/[\\/]/).pop() || '';
    const sanitized = leafName
      // eslint-disable-next-line no-control-regex
      .replace(/[\u0000-\u001f\u007f]/g, '')
      .replace(/[^a-zA-Z0-9._-]/g, '_')
      .replace(/^\.+|\.+$/g, '');
    if (!sanitized || sanitized === '.' || sanitized === '..') return fallback;
    const dotIndex = sanitized.lastIndexOf('.');
    return dotIndex > 0
      ? `${sanitized.substring(0, dotIndex)}${sanitized.substring(dotIndex).toLowerCase()}`
      : sanitized;
  };

  const contentTypeByExtension: Record<string, string> = {
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.png': 'image/png',
    '.webp': 'image/webp',
  };

  const canonicalContentType = (fileName: string) =>
    contentTypeByExtension[fileName.toLowerCase().substring(fileName.lastIndexOf('.'))];

  const getStoragePath = (file: File): string => {
    const timestamp = Date.now();
    return `company-logos/${timestamp}_${sanitizeStorageSegment(file.name)}`;
  };

  const uploadFile = async (file: File) => {
    const uploadState: UploadState = {
      file,
      progress: 0,
      status: 'uploading',
    };

    setUpload(uploadState);

    try {
      const storagePath = getStoragePath(file);
      const contentType = canonicalContentType(storagePath);
      if (!contentType) throw new Error('Tipo de imagem não suportado.');
      const storageRef = ref(storage, storagePath);

      // Start upload
      const uploadTask = uploadBytesResumable(storageRef, file, { contentType });

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUpload((prev) => (prev ? { ...prev, progress, storagePath } : null));
        },
        (error) => {
          console.error('Upload error:', error);
          setUpload((prev) => (prev ? { ...prev, status: 'error', error: error.message } : null));
        },
        async () => {
          // Upload completed
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          setUpload((prev) => (prev ? { ...prev, status: 'completed', downloadURL } : null));
          onLogoChange(downloadURL, storagePath);
        }
      );
    } catch (error) {
      console.error('Upload error:', error);
      setUpload((prev) =>
        prev
          ? {
              ...prev,
              status: 'error',
              error: error instanceof Error ? error.message : 'Unknown error',
            }
          : null
      );
    }
  };

  const handleFileSelect = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    if (isValidImageFile(file)) {
      uploadFile(file);
    } else {
      setUpload({
        file,
        progress: 0,
        status: 'error',
        error: 'Arquivo inválido. Use JPG, PNG ou WebP (máx. 5MB)',
      });
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleRemoveLogo = async () => {
    if (currentLogoUrl) {
      try {
        // Extract storage path from URL
        const url = new URL(currentLogoUrl);
        const pathMatch = url.pathname.match(/\/o\/(.+)\?/);
        if (pathMatch) {
          const storagePath = decodeURIComponent(pathMatch[1]);
          const storageRef = ref(storage, storagePath);
          await deleteObject(storageRef);
        }
      } catch (error) {
        console.error('Error deleting logo:', error);
      }
    }
    onLogoChange(null);
  };

  const handleRemoveUpload = () => {
    setUpload(null);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const displayUrl = upload?.downloadURL || currentLogoUrl;

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Logo da Empresa
      </Typography>

      {displayUrl && (
        <Card sx={{ mb: 2, maxWidth: 200 }}>
          <CardMedia component="img" height="200" image={displayUrl} alt="Logo da empresa" sx={{ objectFit: 'contain' }} />
          <CardContent sx={{ p: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                {upload?.file ? formatFileSize(upload.file.size) : 'Logo atual'}
              </Typography>
              <IconButton size="small" onClick={handleRemoveLogo} disabled={disabled} color="error">
                <DeleteIcon />
              </IconButton>
            </Box>
          </CardContent>
        </Card>
      )}

      {upload && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ImageIcon color="primary" />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" noWrap>
                {upload.file.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatFileSize(upload.file.size)}
              </Typography>
            </Box>
            <IconButton size="small" onClick={handleRemoveUpload} disabled={upload.status === 'uploading'}>
              <DeleteIcon />
            </IconButton>
          </Box>

          {upload.status === 'uploading' && (
            <Box sx={{ mt: 1 }}>
              <LinearProgress variant="determinate" value={upload.progress} />
              <Typography variant="caption" color="text.secondary">
                {Math.round(upload.progress)}% concluído
              </Typography>
            </Box>
          )}

          {upload.status === 'error' && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {upload.error}
            </Alert>
          )}

          {upload.status === 'completed' && (
            <Alert severity="success" sx={{ mt: 1 }}>
              Upload concluído com sucesso!
            </Alert>
          )}
        </Paper>
      )}

      {!displayUrl && !upload && (
        <Paper
          sx={{
            p: 3,
            textAlign: 'center',
            border: '2px dashed',
            borderColor: isDragging ? 'primary.main' : 'grey.300',
            backgroundColor: isDragging ? 'action.hover' : 'background.paper',
            cursor: disabled ? 'default' : 'pointer',
            '&:hover': {
              borderColor: disabled ? 'grey.300' : 'primary.main',
              backgroundColor: disabled ? 'background.paper' : 'action.hover',
            },
          }}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => !disabled && fileInputRef.current?.click()}
        >
          <CloudUploadIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Arraste uma imagem aqui ou clique para selecionar
          </Typography>
          <Typography variant="caption" color="text.secondary">
            JPG, PNG ou WebP (máx. 5MB)
          </Typography>
        </Paper>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        onChange={(e) => handleFileSelect(e.target.files)}
        style={{ display: 'none' }}
        disabled={disabled}
      />
    </Box>
  );
};

export default LogoUpload;
